import { DOCUMENT } from "@angular/common";
import { Inject, Injectable, NgZone, Renderer2, RendererFactory2 } from "@angular/core";
import { animationFrames, distinctUntilChanged, map, Subscription, takeUntil } from "rxjs";

import { MENU_ANCHOR_TARGETS, MENU_PANEL_SELECTOR, MENU_TRIGGER_SELECTOR, sel } from "../onboarding.const";
import { OnboardingService } from "./onboarding.service";
import { OnboardingPositionService } from "./onboarding-position.service";

import { buildElementSelector, ELEMENT_ID } from "@app/core/constants/element-ids.const";
import { DestroyService } from "@app/core/services/destroy.service";
import { IOnboardingStep } from "@app/shared/types/onboarding.type";

const RESOLVE_DELAY = 150;
const TARGET_RETRY_DELAY = 200;
const TARGET_MAX_RETRIES = 8;

const SCROLL_INTO_VIEW_OPTIONS: ScrollIntoViewOptions = {
  behavior: "smooth",
  block: "center",
  inline: "center",
};

const HIGHLIGHT_CLASS = "onboarding-highlight-target";

const INTERACTIVE_STEP_SELECTORS = new Set<string>([
  sel(ELEMENT_ID.NavClients),
  sel(ELEMENT_ID.NavPolicies),
  sel(ELEMENT_ID.NavProducts),
  sel(ELEMENT_ID.UserProfileIcon),
]);

const NAV_GROUP_CHILD_TARGETS: Record<string, string> = {
  [sel(ELEMENT_ID.NavProducts)]: sel(ELEMENT_ID.NavProductsList),
};

const MENU_STEP_BODY_CLASS = "onboarding-menu-step";
const MENU_LOCKED_BODY_CLASS = "onboarding-menu-locked";
const MENU_BACKDROP_SELECTOR = ".cdk-overlay-backdrop";

@Injectable()
export class OnboardingTargetService {
  private readonly renderer: Renderer2;

  private highlightedEl: HTMLElement | null = null;
  private openedMenuTrigger: HTMLElement | null = null;
  private trackSub: Subscription | null = null;

  constructor(
    @Inject(DOCUMENT) private document: Document,
    private onboarding: OnboardingService,
    private position: OnboardingPositionService,
    private zone: NgZone,
    private destroy$: DestroyService,
    rendererFactory: RendererFactory2,
  ) {
    this.renderer = rendererFactory.createRenderer(null, null);
  }

  get highlightedElement(): HTMLElement | null {
    return this.highlightedEl;
  }

  isInteractiveStep(step: IOnboardingStep): boolean {
    return !!step.targetSelector && INTERACTIVE_STEP_SELECTORS.has(buildElementSelector(step.targetSelector));
  }

  isTargetInOpenMenu(step: IOnboardingStep): boolean {
    if (!step.targetSelector) {
      return false;
    }

    const element = this.document.querySelector<HTMLElement>(buildElementSelector(step.targetSelector));

    return !!element?.closest(MENU_PANEL_SELECTOR);
  }

  activate(step: IOnboardingStep, onRect: (rect: DOMRect | null) => void): void {
    setTimeout(() => this.resolveTarget(step, onRect), RESOLVE_DELAY);
  }

  clear(keepMenuOpen = false): void {
    this.stopTracking();

    if (!keepMenuOpen) {
      this.closeOpenedMenu();
    }

    if (this.highlightedEl) {
      this.renderer.removeClass(this.highlightedEl, HIGHLIGHT_CLASS);
      this.highlightedEl = null;
    }
  }

  private resolveTarget(step: IOnboardingStep, onRect: (rect: DOMRect | null) => void, attempt = 0): void {
    if (this.onboarding.currentStep() !== step) {
      return;
    }

    if (!step.targetSelector) {
      onRect(null);

      return;
    }

    const targetSelector = buildElementSelector(step.targetSelector);
    const selector = MENU_ANCHOR_TARGETS[targetSelector]?.anchor ?? this.resolveTargetSelector(targetSelector);
    const element = this.document.querySelector<HTMLElement>(selector);

    if (!element) {
      if (attempt < TARGET_MAX_RETRIES) {
        setTimeout(() => this.resolveTarget(step, onRect, attempt + 1), TARGET_RETRY_DELAY);
      } else {
        this.onboarding.markCurrentStepUnavailable();
      }

      return;
    }

    this.highlightedEl = element;
    this.renderer.addClass(element, HIGHLIGHT_CLASS);
    this.maybeOpenMenu(element, this.isInteractiveStep(step));
    element.scrollIntoView(SCROLL_INTO_VIEW_OPTIONS);

    this.trackTarget(element, step, onRect);
  }

  private trackTarget(element: HTMLElement, step: IOnboardingStep, onRect: (rect: DOMRect | null) => void): void {
    this.stopTracking();

    this.zone.runOutsideAngular(() => {
      this.trackSub = animationFrames()
        .pipe(
          map(() => element.getBoundingClientRect()),
          distinctUntilChanged((a, b) => this.position.rectsMatch(a, b)),
          takeUntil(this.destroy$),
        )
        .subscribe(rect => {
          if (this.onboarding.currentStep() !== step || this.highlightedEl !== element) {
            return;
          }

          this.zone.run(() => onRect(rect));
        });
    });
  }

  private stopTracking(): void {
    this.trackSub?.unsubscribe();
    this.trackSub = null;
  }

  private resolveTargetSelector(targetSelector: string): string {
    const childSelector = NAV_GROUP_CHILD_TARGETS[targetSelector];

    if (!childSelector) {
      return targetSelector;
    }

    this.ensureNavGroupExpanded(targetSelector, childSelector);

    return childSelector;
  }

  private ensureNavGroupExpanded(groupSelector: string, childSelector: string): void {
    if (this.document.querySelector(childSelector)) {
      return;
    }

    this.document.querySelector<HTMLElement>(groupSelector)?.click();
  }

  private maybeOpenMenu(element: HTMLElement, interactive: boolean): void {
    if (!element.matches(MENU_TRIGGER_SELECTOR)) {
      return;
    }

    if (this.openedMenuTrigger === element) {
      return;
    }

    this.renderer.addClass(this.document.body, MENU_STEP_BODY_CLASS);

    if (!interactive) {
      this.renderer.addClass(this.document.body, MENU_LOCKED_BODY_CLASS);
    }

    this.openedMenuTrigger = element;
    element.click();
  }

  private closeOpenedMenu(): void {
    if (this.openedMenuTrigger) {
      const backdrops = this.document.querySelectorAll<HTMLElement>(MENU_BACKDROP_SELECTOR);

      backdrops[backdrops.length - 1]?.click();
      this.openedMenuTrigger = null;
    }

    this.renderer.removeClass(this.document.body, MENU_STEP_BODY_CLASS);
    this.renderer.removeClass(this.document.body, MENU_LOCKED_BODY_CLASS);
  }
}
